import { useMemo, useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";

import { type SLNG } from "@/models";
import { type CHLD } from "@/models/chld";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import { FiPlus } from "react-icons/fi";
import { formatDateOnly } from "@/utils/dates";
import {
  createNamedFilename,
  CSVDownloadButton,
} from "@/components/shared/CSVDownloadButton";
import SLNGEditDialog from "./dialogs/SLNGEditDialog";
import { buildSlngAddDefaults } from "./useSLNGFormLogic";

const columns: GridColumnProps[] = [
  { id: "ProgramYear", field: "ProgramYear", title: "Program Year" },
  { id: "PreschoolName", field: "PreschoolName", title: "School" },
  { id: "LocalChildId", field: "LocalChildId", title: "Local Child ID (CICN)" },
  { id: "CSPPID", field: "CSPPID", title: "CSPPID" },
  {
    id: "EffectiveStartDate",
    field: "EffectiveStartDate",
    title: "Effective Start Date",
  },
];

const csvHeaders = [
  { label: "Program Year", key: "ProgramYear" },
  { label: "School", key: "PreschoolName" },
  { label: "Local Child ID (CICN)", key: "LocalChildId" },
  { label: "CSPPID", key: "CSPPID" },
  { label: "Effective Start Date", key: "EffectiveStartDate" },
];

type SLNGRow = SLNG & {
  PreschoolName?: string;
  ChildName?: string;
  __raw: SLNG;
};

function getMostRecentSLNG(records: SLNG[]): SLNG | undefined {
  if (records.length === 0) return undefined;
  return [...records].sort((a, b) => {
    const da = new Date(a.EffectiveStartDate ?? "").getTime();
    const db = new Date(b.EffectiveStartDate ?? "").getTime();
    return (
      (Number.isNaN(db) ? -Infinity : db) - (Number.isNaN(da) ? -Infinity : da)
    );
  })[0];
}

export default function SLNGRecordsTab({
  slngRecords,
  child,
}: {
  slngRecords: SLNG[];
  child: CHLD;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [popupSLNG, setPopupSLNG] = useState<SLNG | undefined>(undefined);
  const [slngDefaults, setSlngDefaults] = useState<Partial<SLNG> | undefined>(
    undefined
  );

  const canEdit = leaContext.hasRole("SLNGEdit");

  const closeDialog = () => {
    setDialogOpen(false);
    setPopupSLNG(undefined);
    setSlngDefaults(undefined);
  };

  const onAddButtonClick = () => {
    const mostRecent = getMostRecentSLNG(slngRecords);
    const defaults: Partial<SLNG> = mostRecent
      ? buildSlngAddDefaults(mostRecent)
      : {
          LocalChildId: child.LocalChildId ?? "",
          PreschoolCode: child.PreschoolCode ?? "",
          ProgramYear: child.ProgramYear ?? "",
          CSPPID: child.CSPPID ?? "",
        };
    setSlngDefaults(defaults);
    setPopupSLNG(undefined);
    setDialogOpen(true);
  };

  const onEditButtonClick = (c: SLNG) => {
    setSlngDefaults(undefined);
    setPopupSLNG(c);
    setDialogOpen(true);
  };

  const childName = [
    child.ChildFirstName,
    child.ChildMiddleName,
    child.ChildLastName,
  ]
    .filter(Boolean)
    .join(" ");

  const tableData: SLNGRow[] = useMemo(() => {
    return (slngRecords ?? []).map((record) => ({
      ...record,
      ChildName: childName,
      PreschoolName: leaContext?.currentOrgPreschools?.find(
        (x) => x.PreschoolCDSCode === record.PreschoolCode
      )?.PreschoolName,
      EffectiveStartDate: record.EffectiveStartDate
        ? formatDateOnly(record.EffectiveStartDate)
        : "",
      __raw: record,
    }));
  }, [slngRecords, leaContext?.currentOrgPreschools, childName]);

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <TooltipTrigger tooltipText="Export supplemental language records">
          <CSVDownloadButton
            data={tableData}
            headers={csvHeaders}
            filename={createNamedFilename(
              ["ChildName"],
              "supplemental-language-record"
            )}
          />
        </TooltipTrigger>

        {canEdit && (
          <TooltipTrigger tooltipText="Add a supplemental language record">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onAddButtonClick}
            >
              <FiPlus className="me-1" />
              Add
            </button>
          </TooltipTrigger>
        )}
      </div>

      <KendoTableClientState
        data={tableData}
        columns={columns}
        onRowClick={(event: GridRowClickEvent) =>
          onEditButtonClick((event.dataItem as SLNGRow).__raw)
        }
      />

      {dialogOpen && (
        <SLNGEditDialog
          selectedSLNG={popupSLNG}
          slngDefaults={slngDefaults}
          onClose={closeDialog}
        />
      )}
    </>
  );
}
